import { useEffect, useState } from 'react';
import { api } from '../api';
import { colors, fmtDateTime } from './UI';

// Small "Last upload by X at Y" chip surfaced near each uploader on the
// Admin tabs. Single fetch hits /admin/upload-status which returns every
// upload type at once -- callers pick which key they want via the
// `statusKey` prop. The component renders a muted gray chip when there's
// no data yet ("Never uploaded"), and a darker chip with timestamp +
// user once an upload has been recorded.
//
// Usage:
//   <UploadStatusBadge statusKey="last_rentalman_upload" />
//
// The badge takes a `refreshSignal` prop so the parent can force a
// refresh after a successful upload (pass any value that changes -- a
// timestamp, a counter, etc.).
export default function UploadStatusBadge({ statusKey, refreshSignal }) {
  const [status, setStatus] = useState(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    api.getUploadStatus()
      .then(all => setStatus(all?.[statusKey] || null))
      .catch(() => setStatus(null))
      .finally(() => setLoaded(true));
  }, [statusKey, refreshSignal]);

  if (!loaded) return null; // avoid flash of "Never uploaded"

  const base = {
    display: 'inline-flex', alignItems: 'center', gap: 6,
    padding: '4px 10px', borderRadius: 6,
    fontSize: 11, fontWeight: 600,
    border: '1px solid'
  };

  if (!status) {
    return (
      <span style={{ ...base, background: '#F3F4F6', color: '#6B7280', borderColor: '#E5E7EB' }}>
        ⬆ Never uploaded
      </span>
    );
  }

  return (
    <span
      style={{ ...base, background: '#ECFDF5', color: '#065F46', borderColor: '#A7F3D0' }}
      title={status.extra ? JSON.stringify(status.extra, null, 2) : undefined}
    >
      ⬆ Last upload: {fmtDateTime(status.at)}
      {status.user_name && <> by <strong>{status.user_name}</strong></>}
      {Number(status.count) > 0 && <> · {Number(status.count).toLocaleString()} rows</>}
    </span>
  );
}
